/**
 * Phase 2.1 — Coalesce identical stateVersion bursts.
 *
 * The perf probe showed ~19% of battle WebSocket events are byte-identical
 * consecutive resends (e.g. two `game-update` @ v2, two `turn-played` @ v37).
 * Each duplicate is fully parsed, merged and re-rendered for zero state change.
 *
 * This helper suppresses ONLY a message that is byte-identical to the immediately
 * preceding message AND whose type is in a conservative coalescable allowlist.
 *
 * Why byte-identical (not just same stateVersion)?
 * - Identical raw bytes reprocessed produce identical state — provably redundant.
 * - The one-shot VFX consumers at WS entry are idempotent (own dedupe keys), so
 *   they already ran on the first copy; dropping the second changes nothing.
 * - It sidesteps the failure mode that broke earlier slimming work: we never
 *   guess what "didn't change" — we only drop provably-identical payloads.
 *
 * Anything NOT in COALESCABLE_WS_TYPES resets the tracker, so messages with
 * distinct side effects (weapon-attached, reconnect-success, counterstrike-*,
 * state-desync-error, mulligan flows, etc.) are never dropped.
 */

/**
 * Types safe to coalesce: pure authoritative state syncs. A byte-identical
 * consecutive resend of one of these is always redundant work.
 * @type {Set<string>}
 */
export const COALESCABLE_WS_TYPES = new Set(['game-update', 'turn-played']);

/**
 * Decide whether an incoming raw WS message should be suppressed as a
 * byte-identical consecutive duplicate.
 *
 * @param {Object} params
 * @param {string} params.type - Parsed message `type`.
 * @param {string} params.raw - Raw message string (event.data).
 * @param {string|null} params.previousRaw - Raw string of the previously seen coalescable message (null if none / last was non-coalescable).
 * @returns {boolean} true when the message is a redundant duplicate and can be skipped.
 */
export function shouldCoalesceWsMessage({ type, raw, previousRaw }) {
  if (typeof raw !== 'string') {
    throw new Error('shouldCoalesceWsMessage: raw message string is required');
  }
  if (!COALESCABLE_WS_TYPES.has(type)) {
    return false;
  }
  if (previousRaw == null) {
    return false;
  }
  return raw === previousRaw;
}

/**
 * Compute the value to remember for the next comparison.
 * Only coalescable types are remembered; every other type resets the tracker to
 * null so a following coalescable message is never compared against it.
 *
 * @param {Object} params
 * @param {string} params.type - Parsed message `type`.
 * @param {string} params.raw - Raw message string (event.data).
 * @returns {string|null} the raw string to store, or null to reset the tracker.
 */
export function nextCoalesceRaw({ type, raw }) {
  if (!COALESCABLE_WS_TYPES.has(type)) {
    return null;
  }
  return typeof raw === 'string' ? raw : null;
}
